import styles from "@/components/popup-shell.module.css";
import { globals } from "@/globals";
import * as signals from "@/signals";
import { Dialog, DialogTitle } from "@headlessui/react";
import clsx from "clsx";
import { t } from "i18next";
import { JSX, MouseEvent, ReactNode, useEffect, useState } from "react";

interface PopupShellProps {
	children: ReactNode;
	isCloseDisabled?: boolean;
	isClosingOverride?: boolean;
	isHeadingHidden?: boolean;
	isMaximizedByDefault?: boolean;
	isMaximizeEnabled?: boolean;
	isPaddingNarrow?: boolean;
	isPureWhite?: boolean;
	isShown: boolean;
	onClose: () => void;
	title: string;
}

function PopupShell({
	children,
	isCloseDisabled,
	isClosingOverride,
	isHeadingHidden,
	isMaximizedByDefault,
	isMaximizeEnabled,
	isShown,
	isPaddingNarrow,
	isPureWhite,
	onClose,
	title,
}: PopupShellProps): JSX.Element {
	const [isCloseButtonHovered, setIsCloseButtonHovered] =
		useState<boolean>(false);
	const [isClosing, setIsClosing] = useState<boolean>(false);
	const [isMaximized, setIsMaximized] = useState<boolean>(
		isMaximizedByDefault ? true : false,
	);
	const [isShownAnimationReady, setisShownAnimationReady] =
		useState<boolean>(true);
	const [isPopupContentScrolled, setIsPopupContentScrolled] =
		useState<boolean>(false);

	const isActuallyClosing = isClosingOverride ?? isClosing;

	const handleClose = (): void => {
		if (isCloseDisabled) {
			return;
		}
		if (isClosingOverride !== undefined) {
			onClose();
			return;
		}
		setIsClosing(true);
		window.setTimeout(() => {
			onClose();
			setIsClosing(false);
			setIsCloseButtonHovered(false);
			setIsPopupContentScrolled(false);
			setisShownAnimationReady(true);
		}, 350);
	};

	const handleCloseButtonMouseEnter = (): void => {
		setIsCloseButtonHovered(true);
	};

	const handleCloseButtonMouseLeave = (): void => {
		setIsCloseButtonHovered(false);
	};

	const handleMaximize = (): void => {
		if (!isMaximizeEnabled) {
			return;
		}
		setIsMaximized(!isMaximized);
	};

	const handleScroll = (event: MouseEvent<HTMLDivElement>): void => {
		setIsPopupContentScrolled(event.currentTarget.scrollTop > 5);
	};

	useEffect(() => {
		if (!isShown) {
			return;
		}
		window.setTimeout(() => {
			setisShownAnimationReady(false);
		}, 350);
	}, [isShown]);

	return (
		<Dialog
			onClose={handleClose}
			open={isShown}
		>
			<div
				className={clsx(
					"overlay",
					styles["popup-overlay"],
					signals.generalSettings.value.isBackdropBlurEnabled &&
						!signals.generalSettings.value.isLiteModeEnabled &&
						!globals.isSafari && // Safari's backdrop filter conflicts with transform
						"backdrop-blur",
					!isActuallyClosing && "fade-in",
					isActuallyClosing && "fade-out",
				)}
			>
				<div
					className={clsx(
						styles["popup"],
						isPureWhite && styles["pure-white"],
						isMaximized && styles["maximized"],
						isPaddingNarrow && styles["narrow-padding"],
						isPopupContentScrolled && styles["scrolled"],
						isShownAnimationReady &&
							!signals.generalSettings.value.shouldReduceMotion &&
							styles["show"],
						isCloseButtonHovered &&
							!isActuallyClosing &&
							!isMaximized &&
							!signals.generalSettings.value.shouldReduceMotion &&
							styles["tilted"],
						isActuallyClosing &&
							!signals.generalSettings.value.shouldReduceMotion &&
							styles["closing"],
						isMaximized && styles["lite"],
					)}
				>
					<header
						className={styles["title-bar"]}
						onDoubleClick={handleMaximize}
					>
						{title}
						<div className={styles["action-buttons-container"]}>
							{isMaximizeEnabled && (
								<button
									className={styles["action-button"]}
									onClick={handleMaximize}
									title={
										isMaximized
											? t("restore")
											: t("maximize")
									}
									type="button"
								>
									{isMaximized ? (
										<i>&#xe7a7;</i>
									) : (
										<i>&#xe6c0;</i>
									)}
								</button>
							)}
							{!isCloseDisabled && (
								<button
									className={styles["action-button"]}
									onClick={handleClose}
									onMouseEnter={handleCloseButtonMouseEnter}
									onMouseLeave={handleCloseButtonMouseLeave}
									title={t("close")}
									type="button"
								>
									<i>&#xe689;</i>
								</button>
							)}
						</div>
					</header>
					<div
						className={styles["popup-content"]}
						onScroll={handleScroll}
					>
						{!isHeadingHidden && (
							<DialogTitle
								as="h1"
								className={styles["popup-title"]}
							>
								{title}
							</DialogTitle>
						)}
						{children}
					</div>
				</div>
			</div>
		</Dialog>
	);
}

export default PopupShell;
